import type { Treaty } from '@elysiajs/eden'
import type { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/vue-query'
import type { app as application, serializers } from '@timeshare-reminders/server'
import { EdenFetchError, treaty } from '@elysiajs/eden'
import {

  useMutation as useTanstackMutation,

  useQuery as useTanstackQuery,
} from '@tanstack/vue-query'
import * as devalue from 'devalue'

export const serializer: serializers = 'devalue'

/**
 * Reads the body according to what the server actually sent. The server opts into `devalue` when the
 * request carries the `serializer` header, and advertises it back with `json-serializer`.
 */
async function deserialize(response: Response) {
  const contentType = response.headers.get('Content-Type') ?? ''

  if (contentType.includes('application/json')) {
    const json = await response.json()
    if (response.headers.get('json-serializer') === 'devalue') {
      return devalue.parse(json as string)
    }
    return json
  }

  if (contentType.includes('multipart/form-data')) {
    return response.formData()
  }

  return undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type InferBody<T extends (...args: any[]) => any>
  = Parameters<T>[0] extends { body: infer B } ? B : Parameters<T>[0]

const edenAPI = treaty<application>(import.meta.env.VITE_API_URL, {
  async onResponse(response) {
    const value = await deserialize(response)

    if (!response.ok) {
      /**
       * Must be an `EdenFetchError`: eden wraps anything else thrown here in `EdenFetchError(422, …)`,
       * which is why every failure used to surface as a 422 regardless of the real status.
       */
      throw new EdenFetchError(response.status, value)
    }

    return value
  },

  headers: {
    Origin: import.meta.env.VITE_API_URL,
    serializer,
  },
  fetch: {
    credentials: 'include',
  },
  parseDate: false,
}).api.v1

function useQuery<T extends Record<number, unknown> = Record<number, unknown>>(
  queryKey: QueryKey,
  treatyFn: () => Promise<Treaty.TreatyResponse<T>>,
  options?: Omit<
    UseQueryOptions<
      Treaty.Data<Treaty.TreatyResponse<T>>,
      Treaty.Error<Treaty.TreatyResponse<T>>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useTanstackQuery<
    Treaty.Data<Treaty.TreatyResponse<T>>,
    Treaty.Error<Treaty.TreatyResponse<T>>
  >({
    queryKey,
    queryFn: async () => {
      const response = await treatyFn()
      if (response.error) {
        throw response.error
      }

      if (response.data !== undefined) {
        return response.data as Treaty.Data<Treaty.TreatyResponse<T>>
      }

      throw new Error('No data returned from API')
    },
    ...options,
  })
}

function useMutation<
  TFn extends (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    args: any,
  ) => Promise<Treaty.TreatyResponse<Record<number, unknown>>>,
>(
  treatyFn: TFn,
  options?: Omit<
    UseMutationOptions<
      Treaty.Data<Awaited<ReturnType<TFn>>>,
      Treaty.Error<Awaited<ReturnType<TFn>>>,
      Parameters<TFn>[0]
    >,
    'mutationFn'
  >,
) {
  return useTanstackMutation<
    Treaty.Data<Awaited<ReturnType<TFn>>>,
    Treaty.Error<Awaited<ReturnType<TFn>>>,
    Parameters<TFn>[0]
  >({
    mutationFn: async (variables) => {
      const response = await treatyFn(variables)

      if (response.error) {
        throw response.error
      }

      if (response.data !== undefined) {
        return response.data as Treaty.Data<Awaited<ReturnType<TFn>>>
      }

      throw new Error('No data returned from API')
    },
    ...options,
  })
}

export { edenAPI as api, useMutation, useQuery }
